import { GameMode } from 'constants/gamePresets';
import { IUser } from './User';

export enum EGameType {
  FARITANY = 'FARITANY',
  TSIPIKA = 'TSIPIKA',
}

export enum ERoundStatus {
  NOT_STARTED = 'NOT_STARTED',
  IN_PROGRESS = 'IN_PROGRESS',
  FINISHED = 'FINISHED',
}

/** Méthode de fin de partie (alignée backend `EWinMethod`). */
export type WinMethod =
  | 'SCORE'
  | 'TIMEOUT'
  | 'DISCONNECTION'
  | 'SCORE_TIE'
  | 'REFLECTION_TIME';
export interface IRound {
  id?: number;
  gridSize: number;
  /** Durée max partie en secondes (900, 1200, 1800). */
  globalDurationSeconds?: number;
  roomName: string;
  maxScore: number;
  winner?: IUser;
  mise: number;
  invitedId?: number;
  invited?: IUser;
  creatorId?: number;
  creator?: IUser;
  invitationFor?: IUser;
  /** Unix ms — expiration invitation (serveur). */
  invitationExpiresAt?: number;
  isGameOver?: boolean;
  isCanPlaceTwoPoint?: boolean;
  /** Mode de jeu : défaut CLASSIC côté serveur si absent. */
  gameMode?: GameMode;
  /** Type de jeu : FARITANY (défaut) ou TSIPIKA. */
  gameType?: EGameType;
  status: ERoundStatus;
  createdAt?: Date | string;
  updatedAt?: Date | string;
}

export interface IPoint {
  x: number;
  y: number;
  owner: Partial<IPlayer>;
  isCaptured: boolean;
}

export interface IPlayer {
  id: number;
  score: number;
  pseudo: string;
  isAbsent: boolean;
  userId: number;
  roundId: number;
  color: string;
  avatar?: string;
}

export interface ISvgPathPlayer {
  id: number;
  userId: number;
  pseudo: string;
  color: string;
}

export interface ISvgPath {
  id: number;
  pathString: string;
  player: ISvgPathPlayer;
  isShow: boolean;
  points?: IPoint[];
  pointsCaptured?: IPoint[];
}

export interface ITimerDeadlinePayload {
  expiresAt: number;
  duration: number;
}

export interface ITimerSyncPayload {
  scope: 'turn' | 'global' | 'disconnect';
  expiresAt: number;
  duration: number;
}

export interface ITimersSnapshotPayload {
  turn: ITimerDeadlinePayload | null;
  global: ITimerDeadlinePayload | null;
  disconnect: ITimerDeadlinePayload | null;
}

export interface IRoundGame {
  gridSize: number;
  grid: (number | null)[][];
  players: IPlayer[];
  currentPlayerIndex: number;
  roomName: string;
  closedPoints: IPoint[][];
  svgPaths: ISvgPath[];
  pointsCaptured: IPoint[];
  listPoints: IPoint[];
  playerTurn: IPlayer;
  isGameOver: boolean;
  maxScore: number | null;
  winner: IPlayer | null;
  duration: number;
  durationWhenPlayerIsAbsent: number;
  newPoint: IPoint;
  gridNumber: (number | null)[][];
  newSvgPaths: ISvgPath[];
  updatedSvgPaths: ISvgPath;
  updatedPlayers: {
    id: number;
    score: number;
    isAbsent: boolean;
    userId: number;
  }[];
  isDraw: boolean;
  /** Méthode de fin de partie (renseignée à la fin par le backend). */
  winMethod?: WinMethod | null;
  globalReamingTimeLeft: number;
  turnPlayerTimerLeft: number;
  timeLeft: number;
  pointsCapturedNotForPlayer: IPoint[];
  closedPointsTransparentPoint: {
    points: IPoint[][];
    pointsCaptured: IPoint[];
  }[];
  mise: number;
  /** Durée max partie en secondes (900, 1200, 1800). */
  globalDurationSeconds?: number;
  creator: Partial<IUser>;
  /** Mode de jeu hérité de la partie. */
  gameMode?: GameMode;
  /** Unix ms — expiration timer tour (serveur), si actif */
  turnExpiresAt?: number | null;
  /** Unix ms — expiration timer partie globale */
  globalExpiresAt?: number | null;
  /** Unix ms — expiration timer absence adversaire */
  disconnectExpiresAt?: number | null;
}

/**
 * Payload delta reçu du serveur sur l'event 'clickedPoint'.
 * Seuls les champs ayant changé durant le tour sont présents.
 * Toujours présents : newPoint, playerTurn.
 */
export interface IClickedPointDelta {
  newPoint: IPoint;
  playerTurn: IPlayer;
  updatedPlayers?: { id: number; score: number; isAbsent: boolean; userId: number }[];
  newSvgPaths?: ISvgPath[];
  updatedSvgPaths?: ISvgPath;
  isGameOver?: true;
  winner?: IPlayer | null;
  isDraw?: true;
  winMethod?: WinMethod | null;
}

export interface IRoundReplayGame {
  gridSize: number;
  grid: (number | null)[][];
  players: IPlayer[];
  roomName: string;
  svgPaths: ISvgPath[];
}

/** Données minimales nécessaires pour rendre une feuille Faritany (live ou replay). */
export type FaritanySheetData = Pick<
  IRoundGame,
  'gridSize' | 'grid' | 'players' | 'svgPaths'
> & {
  newPoint?: IPoint | null;
};
